import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { ShareButtons } from "@/components/ShareButtons";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Star, MapPin, Store as StoreIcon, Users, Heart, BadgeCheck } from "lucide-react";

export default function StorePage() {
  const { sellerId } = useParams();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    setStore(null); setNotFound(false);
    api.get(`/stores/${sellerId}`).then((r) => setStore(r.data)).catch(() => setNotFound(true));
  }, [sellerId]);

  useEffect(() => {
    if (user && sellerId && user.user_id !== sellerId) {
      api.get(`/stores/${sellerId}/follow`).then((r) => setFollowing(!!r.data.following)).catch(() => {});
    }
  }, [user, sellerId]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow this store"); return; }
    try {
      if (following) { await api.delete(`/stores/${sellerId}/follow`); setFollowing(false); toast.success("Unfollowed store"); }
      else { await api.post(`/stores/${sellerId}/follow`); setFollowing(true); toast.success("Following — new arrivals will show in your feed"); }
    } catch { toast.error("Something went wrong"); }
  };

  if (notFound) return (
    <Layout seo={{ title: "Store not found", noindex: true }}>
      <div className="max-w-5xl mx-auto px-4 py-24 text-center">
        <StoreIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="font-display font-bold text-2xl">Store not found</h1>
        <p className="text-sm text-muted-foreground mt-1">This store may have moved or is no longer available.</p>
      </div>
    </Layout>
  );

  if (!store) return <Layout><div className="py-24 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div></Layout>;

  const desc = store.shop_description || `Shop authentic African products from ${store.business_name} on Xurray Mart — delivered across Europe.`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Store",
    name: store.business_name, description: desc,
    aggregateRating: store.seller_rating ? { "@type": "AggregateRating", ratingValue: store.seller_rating, bestRating: 5 } : undefined,
    address: store.city || store.country ? { "@type": "PostalAddress", addressLocality: store.city, addressCountry: store.country } : undefined,
  };

  return (
    <Layout seo={{ title: store.business_name, description: desc, image: store.avatar || undefined, type: "profile", jsonLd }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Store header */}
        <Card className="p-6 mb-8" data-testid="store-header">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center overflow-hidden shrink-0">
              {store.avatar ? <img src={store.avatar} alt={store.business_name} className="w-full h-full object-cover" />
                : <StoreIcon className="w-7 h-7" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-bold text-2xl sm:text-3xl" data-testid="store-name">{store.business_name}</h1>
                {store.is_verified && (
                  <Badge data-testid="store-verified-badge" className="bg-emerald-600 hover:bg-emerald-600 text-white border-0 gap-1">
                    <BadgeCheck className="w-3.5 h-3.5" />Verified Seller
                  </Badge>
                )}
                {store.paused && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Briefly unavailable</Badge>}
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
                {store.seller_rating > 0 && (
                  <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{Number(store.seller_rating).toFixed(1)}</span>
                )}
                {(store.city || store.country) && (
                  <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{[store.city, store.country].filter(Boolean).join(", ")}</span>
                )}
                <span className="inline-flex items-center gap-1"><Users className="w-4 h-4" />{store.followers} follower{store.followers !== 1 ? "s" : ""}</span>
                <span>{store.product_count} product{store.product_count !== 1 ? "s" : ""}</span>
              </div>
              {store.shop_description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{store.shop_description}</p>}
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              {user?.user_id !== store.seller_id && (
                <Button data-testid="store-follow-btn" variant={following ? "outline" : "default"} className="rounded-full" onClick={toggleFollow}>
                  <Heart className={`w-4 h-4 mr-1.5 ${following ? "fill-primary text-primary" : ""}`} />
                  {following ? "Following" : "Follow store"}
                </Button>
              )}
              <ShareButtons title={`${store.business_name} on Xurray Mart`} url={`${process.env.REACT_APP_BACKEND_URL}/api/share/store/${sellerId}`} />
            </div>
          </div>
        </Card>

        {/* Products */}
        {store.paused ? (
          <Card className="p-8 text-center" data-testid="store-paused">
            <p className="text-sm text-muted-foreground">This store is briefly unavailable. Follow it to be notified when it's back online.</p>
          </Card>
        ) : store.products.length === 0 ? (
          <Card className="p-8 text-center"><p className="text-sm text-muted-foreground">No products listed yet — check back soon.</p></Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="store-products">
            {store.products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
